import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { CustomModal, Button, TableList, Page, Spinner, PageErrorState, PageContentHeader } from 'seagull';
import { useEventReporter } from '~/event-reporter';
import { useOnMount } from '~/hooks/useOnMount';
import { useLocaleKeys } from '~/i18n';
import { modalSize } from '~/infra/modals';
import { sendErrReport } from '~/infra/utils';
import { selectors } from '~/store/state-selectors';
import { useStore, useStoreActions } from '~/store/store-context';
import { getAccountingDetails } from '../accounting.utils';
import styles from './MapAccountsModal.module.scss';
import { columnDefs, makeHeaderItems } from './MapAccountsModal.utils';
import { MapAccountsRow } from './MapAccountsRow';
export function MapAccountsModal(param) {
    let { dismissModal, accounting } = param;
    const { t: texts } = useLocaleKeys('integrations.accounting');
    const { t: mapTexts } = useLocaleKeys('integrations.mapAccountsModal');
    const { api, logBI, fetchIntegrations } = useStoreActions();
    const { reportToHeap } = useEventReporter();
    const accountingIntegration = useStore(selectors.integration.accounting(accounting).integration);
    const [modalState, setModalState] = useState('loading');
    const [accountingAccounts, setAccountingAccounts] = useState();
    const userInfo = useStore((state)=>state.userInfo);
    const { testIdPrefix, biCodes } = getAccountingDetails(accounting);
    async function fetchAccounts() {
        setModalState('loading');
        try {
            const data = await api.accounting.getAccounts({
                accounting
            });
            setAccountingAccounts(data);
            setModalState('valid');
        } catch (err) {
            setModalState('error');
        }
    }
    async function onLink(linkType, account) {
        const isPaymentAccount = linkType !== 'expense';
        const biEventCode = isPaymentAccount ? biCodes.linkAccountToPaymentAccount : biCodes.linkAccount;
        logBI(biEventCode, {
            vendorId: userInfo === null || userInfo === void 0 ? void 0 : userInfo.businessId(),
            userId: userInfo === null || userInfo === void 0 ? void 0 : userInfo.id()
        });
        const heapEventName = isPaymentAccount ? "".concat(testIdPrefix, "-select-payments-account") : "".concat(testIdPrefix, "-select-anchor-fees-account");
        reportToHeap('integrations', 'click', heapEventName);
        try {
            await api.accounting.linkAccount({
                accounting,
                data: {
                    linkType,
                    integratedId: account === null || account === void 0 ? void 0 : account.id
                }
            });
            await fetchIntegrations();
        } catch (error) {
            sendErrReport('Failed to link service template', error);
            throw error;
        }
    }
    useOnMount(()=>{
        fetchAccounts();
    });
    return /*#__PURE__*/ _jsx(CustomModal, _object_spread_props(_object_spread({
        bi: {
            closeButton: {
                name: 'map_accounts_modal_close'
            }
        },
        testId: "".concat(testIdPrefix, "-map-accounts-modal"),
        onClose: dismissModal
    }, modalSize.large), {
        footer: /*#__PURE__*/ _jsx("div", {
            className: styles.footer,
            children: /*#__PURE__*/ _jsx(Button, {
                bi: {
                    name: 'close_map_accounts_modal'
                },
                onClick: dismissModal,
                testId: "".concat(testIdPrefix, "-map-accounts-close"),
                children: mapTexts.done()
            })
        }),
        children: /*#__PURE__*/ _jsx(TableList.Provider, {
            columnDefs: columnDefs,
            children: /*#__PURE__*/ _jsxs(Page, {
                height: "100%",
                header: mapTexts.accountsHeader({
                    accountingName: getAccountingDetails(accounting).fullName
                }),
                subheader: texts.mapAccountsSubheader(),
                minWidth: "880px",
                skin: "secondary",
                children: [
                    modalState === 'loading' && /*#__PURE__*/ _jsx(LoadingState, {}),
                    modalState === 'error' && /*#__PURE__*/ _jsx(ErrorState, {
                        onRefresh: fetchAccounts
                    }),
                    modalState === 'valid' && accountingAccounts && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(PageContentHeader, {
                                children: /*#__PURE__*/ _jsx(TableList.Header, {
                                    items: makeHeaderItems(mapTexts)
                                })
                            }),
                            /*#__PURE__*/ _jsx(MapAccountsRow, {
                                anchorAccount: mapTexts.anchorAccountBank(),
                                tooltip: texts.mapAccountsBankTooltip(),
                                selectedAccountId: accountingIntegration === null || accountingIntegration === void 0 ? void 0 : accountingIntegration.getBankAccountId(),
                                accounts: accountingAccounts.bank,
                                onLink: (account)=>onLink('bank', account),
                                testId: "".concat(testIdPrefix, "-map-bank-account-row"),
                                noOptionsText: mapTexts.noOptionsTextBank(),
                                accounting: accounting
                            }),
                            /*#__PURE__*/ _jsx(MapAccountsRow, {
                                anchorAccount: mapTexts.anchorAccountExpense(),
                                tooltip: texts.mapAccountsExpenseTooltip(),
                                selectedAccountId: accountingIntegration === null || accountingIntegration === void 0 ? void 0 : accountingIntegration.getExpenseAccountId(),
                                accounts: accountingAccounts.expense,
                                onLink: (account)=>onLink('expense', account),
                                testId: "".concat(testIdPrefix, "-map-expense-account-row"),
                                accounting: accounting
                            }),
                            /*#__PURE__*/ _jsx(MapAccountsRow, {
                                anchorAccount: mapTexts.anchorAccountClearing(),
                                tooltip: texts.mapAccountsClearingTooltip(),
                                selectedAccountId: accountingIntegration === null || accountingIntegration === void 0 ? void 0 : accountingIntegration.getClearingAccountId(),
                                accounts: accountingAccounts.clearing,
                                onLink: (account)=>onLink('clearing', account),
                                testId: "".concat(testIdPrefix, "-map-clearing-account-row"),
                                accounting: accounting
                            })
                        ]
                    })
                ]
            })
        })
    }));
}
const LoadingState = ()=>/*#__PURE__*/ _jsx("div", {
        className: styles.loading,
        children: /*#__PURE__*/ _jsx(Spinner, {
            size: 40,
            testId: "map-accounts-loading"
        })
    });
const ErrorState = (param)=>{
    let { onRefresh } = param;
    const { t: texts } = useLocaleKeys('integrations.accounting');
    return /*#__PURE__*/ _jsx(PageErrorState, {
        testId: "map-accounts-error-state",
        message: texts.mapAccountsError(),
        onRefresh: onRefresh,
        skin: "secondary"
    });
};
